import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import { LANGUAGES, useAppStore } from '@restorant/core';
import type { StringKey, TextSize, ThemeMode } from '@restorant/core';
import { TEXT_SCALES, radius, scaleFont, useColors } from '../theme';
import { useT } from '../i18n';
import { Icon } from '../icons/Icon';
import { AccountRow } from './AccountRow';
import { Sheet } from './Sheet';
import { T } from './Text';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  const colors = useColors();
  return (
    <View style={{ gap: 9 }}>
      <T size={11.5} weight={700} color={colors.textFaint} style={{ letterSpacing: 0.5 }}>
        {label.toUpperCase()}
      </T>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>{children}</View>
    </View>
  );
}

function Option({
  label,
  active,
  onPress,
  testID,
  previewScale,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
  testID?: string;
  /** Renders the label at this scale, for the text-size row. */
  previewScale?: number;
}) {
  const colors = useColors();
  return (
    <Pressable
      onPress={onPress}
      testID={testID}
      style={({ hovered }: any) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 7,
        paddingVertical: 11,
        paddingHorizontal: 14,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: active ? colors.gold : colors.line,
        backgroundColor: active || hovered ? colors.surface2 : 'transparent',
      })}
    >
      <T
        size={13}
        weight={active ? 700 : 600}
        color={active ? colors.gold : colors.textDim}
        style={previewScale ? { fontSize: scaleFont(13, previewScale) } : undefined}
      >
        {label}
      </T>
      {active ? <Icon name="check" size={14} color={colors.gold} /> : null}
    </Pressable>
  );
}

/**
 * Language, theme and reading size in one sheet. A phone header has no room for
 * three separate chips beside the page title — they pushed it to "Dashboa…".
 */
export function SettingsButton({ compact = false }: { compact?: boolean }) {
  const colors = useColors();
  const t = useT();
  const [open, setOpen] = useState(false);

  const lang = useAppStore((s) => s.lang);
  const setLang = useAppStore((s) => s.setLang);
  const theme = useAppStore((s) => s.theme);
  const setTheme = useAppStore((s) => s.setTheme);
  const textSize = useAppStore((s) => s.textSize);
  const setTextSize = useAppStore((s) => s.setTextSize);

  const themes: { id: ThemeMode; key: StringKey; icon: 'sun' | 'moon' }[] = [
    { id: 'light', key: 'theme_light', icon: 'sun' },
    { id: 'dark', key: 'theme_dark', icon: 'moon' },
  ];

  return (
    <>
      <Pressable
        onPress={() => setOpen(true)}
        testID="settings-button"
        accessibilityLabel={t('settings')}
        style={({ hovered }: any) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: 7,
          paddingVertical: 8,
          paddingHorizontal: compact ? 9 : 12,
          borderRadius: radius.pill,
          borderWidth: 1,
          borderColor: hovered ? colors.gold : colors.line,
          backgroundColor: colors.surface,
        })}
      >
        <Icon name="settings" size={15} color={colors.textDim} />
        {compact ? null : (
          <T size={12.5} weight={700} color={colors.textDim}>
            {t('settings')}
          </T>
        )}
      </Pressable>

      <Sheet visible={open} title={t('settings')} onClose={() => setOpen(false)}>
        <Row label={t('language')}>
          {LANGUAGES.map((l) => (
            <Option
              key={l.id}
              label={l.label}
              active={l.id === lang}
              testID={`set-lang-${l.id}`}
              onPress={() => setLang(l.id)}
            />
          ))}
        </Row>

        <Row label={t('appearance')}>
          {themes.map((th) => (
            <Option
              key={th.id}
              label={t(th.key)}
              active={th.id === theme}
              testID={`set-theme-${th.id}`}
              onPress={() => setTheme(th.id)}
            />
          ))}
        </Row>

        <Row label={t('text_size')}>
          {TEXT_SCALES.map((s) => (
            <Option
              key={s.id}
              label={t(s.labelKey as StringKey)}
              active={s.id === textSize}
              testID={`set-size-${s.id}`}
              previewScale={s.value}
              onPress={() => setTextSize(s.id as TextSize)}
            />
          ))}
        </Row>

        <Row label={t('account')}>
          <View style={{ flexGrow: 1 }}>
            <AccountRow />
          </View>
        </Row>
      </Sheet>
    </>
  );
}
